import {
  Avatar,
  Paper,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  green,
  indigo,
  orange,
  red,
  teal,
  purple,
  brown,
  cyan,
} from '@material-ui/core/colors';
import {
  FilterList as FilterListIcon,
  Sort as SortIcon,
  GetApp as GetAppIcon,
} from '@material-ui/icons';
import _ from 'lodash';
import React, { Fragment, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  Brush,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSnackbar } from '../../Snackbar';
import {
  FETCH_PERSON_DAILY_ACTIVITY,
  FETCH_PERSON_DAILY_ACTIVITY_CANCELLED,
  LOAD_PERSON_DAILY_ACTIVITY,
  SORT_PERSON_DAILY_ACTIVITY,
} from '../../../actions';
import Container from '../../Container';
import {
  Parameters,
  SelectMultiple,
  TablePagination,
  Table,
  CustomTooltip,
} from '../../controls';
import { getKeyLabel, getTextWidth } from '../../../data/constants';
import { downloadCSV } from '../../../apis/utilities';

const {
  useReducedResourceInformation,
  baseType,
  showDoubleCrews,
  rowsPerPageOptions,
} = window.config;

const useStyles = makeStyles((theme) => ({
  itemSection: {
    width: '100%',
    height: 'calc(100vh - 48px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    '& > :last-child': {
      marginBottom: theme.spacing(1),
    },
  },
  section: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  card: {
    margin: theme.spacing(0, 1),
    minWidth: 240,
    fontSize: 12,
  },
  description: {
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  cardContent: {
    padding: 0,
    paddingTop: theme.spacing(4),
  },
  filterField: {
    width: 144,
  },
  groupByField: {
    margin: theme.spacing(0.5),
  },
  typeField: {
    margin: theme.spacing(0.5),
    marginLeft: 0,
  },
  progress: {
    margin: theme.spacing(1),
  },
  textField: {
    margin: theme.spacing(0.5),
    width: 200,
  },
  tableSelect: {
    fontSize: '0.8125rem',
  },
  placeholder: {
    color: theme.palette.grey[500],
  },
  collapseContainer: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  flex: {
    flexGrow: 1,
  },
  legend: {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  legendItem: {
    padding: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  legendAvatar: {
    width: 12,
    height: 12,
    marginRight: theme.spacing(0.5),
  },
  tableContainer: {
    height: 'calc(100vh - 164px)',
    overflowY: 'scroll',
  },
  table: {
    minWidth: 750,
  },
  parameters: {
    width: 280,
  },
  toolbar: {
    padding: theme.spacing(1, 1, 1, 2),
  },
}));

const bars = [
  { name: 'onRadio', colour: green[500] },
  { name: 'driving', colour: purple[500] },
  { name: `inBase`, colour: brown[500] },
  { name: 'inHomeWard', colour: orange[500] },
  { name: 'respondingToIncidents', colour: red[500] },
  { name: 'attendingObjectives', colour: teal[500] },
  ...(showDoubleCrews ? [{ name: 'doubleCrewing', colour: cyan[500] }] : []),
];

const measureTypes = ['total', 'average', 'daily'];

const labels = new Map([
  ['onRadio', 'On Radio'],
  ['driving', 'Driving'],
  ['inBase', `In ${baseType.label}`],
  ['inHomeWard', 'In Home Ward'],
  ['respondingToIncidents', 'Responding to Incidents'],
  ['attendingObjectives', 'Attending Objectives'],
  ['doubleCrewing', 'Double Crewing'],
  ['totalDrivingMileage', 'Total Driving Mileage'],
  ['averageDrivingMileage', 'Average Driving Mileage'],
  ['dailyDrivingMileage', 'Daily Driving '],
  ['totalBaseVisits', `Total ${baseType.label} Visits`],
  ['averageBaseVisits', `Average ${baseType.label} Visits`],
  ['dailyBaseVisits', `Daily ${baseType.label} Visits`],
]);

export default function DailyActivity(props) {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.activity.daily.data, _.isEqual);
  const isLoading = useSelector((state) => state.activity.daily.isLoading);
  const error = useSelector((state) => state.activity.daily.error);
  const groupBy = useSelector((state) => state.activity.daily.groupBy);
  const groupByValues = useSelector(
    (state) => state.activity.daily.groupByValues,
    _.isEqual
  );
  const filter = useSelector((state) => state.activity.daily.filter, _.isEqual);
  const filterValues = useSelector(
    (state) => state.activity.daily.filterValues,
    _.isEqual
  );
  const orderByValues = useSelector(
    (state) => state.activity.daily.orderByValues,
    _.isEqual
  );
  const [showFilter, setShowFilter] = useState(false);
  const [hiddenBars, setHiddenBars] = useState([]);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [mileageType, setMileageType] = useState('totalDrivingMileage');
  const [baseVisitCountType, setBaseVisitCountType] = useState(
    'totalBaseVisits'
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('Group');
  const classes = useStyles();
  const snackbar = useSnackbar();

  const headers = [
    {
      label: `${getKeyLabel(groupBy)} (Count)`,
      key: 'group',
      type: 'text',
    },
    {
      label: 'On Radio (hours)',
      key: 'onRadio',
      type: 'number',
    },
    {
      label: `In ${baseType.label} (hours)`,
      key: `inBase`,
      type: 'number',
    },
    {
      label: 'Responding to Incidents (hours)',
      key: 'respondingToIncidents',
      type: 'number',
    },
    {
      label: 'Attending Objectives (hours)',
      key: 'attendingObjectives',
      type: 'number',
    },
    {
      label: 'Driving (hours)',
      key: 'driving',
      type: 'number',
    },
    {
      label: 'In Home Ward (hours)',
      key: 'inHomeWard',
      type: 'number',
    },
    {
      label: 'Double Crewing (hours)',
      key: 'doubleCrewing',
      type: 'number',
    },
    {
      label: 'Total Driving Mileage',
      key: 'totalDrivingMileage',
      type: 'number',
    },
    {
      label: 'Average Driving Mileage',
      key: 'averageDrivingMileage',
      type: 'number',
    },
    {
      label: 'Daily Driving Mileage',
      key: 'dailyDrivingMileage',
      type: 'number',
    },
    {
      label: `Total ${baseType.label} Visits`,
      key: `totalBaseVisits`,
      type: 'number',
    },
    {
      label: `Average ${baseType.label} Visits`,
      key: `averageBaseVisits`,
      type: 'number',
    },
    {
      label: `Daily ${baseType.label} Visits`,
      key: `dailyBaseVisits`,
      type: 'number',
    },
  ];

  useEffect(() => {
    if (error) {
      snackbar.notify('error', error);
    }
  }, [error, snackbar]);

  useEffect(() => {
    dispatch({
      type: LOAD_PERSON_DAILY_ACTIVITY,
      payload: { filter, groupBy },
    });
  }, [filter, groupBy, dispatch]);

  function handleFilterToggle() {
    setShowFilter(!showFilter);
  }

  function handleFetch(event, query) {
    dispatch({
      type: FETCH_PERSON_DAILY_ACTIVITY,
      payload: { query, filter, groupBy },
    });
  }

  function handleCancel() {
    dispatch({
      type: FETCH_PERSON_DAILY_ACTIVITY_CANCELLED,
    });
  }

  function handleGroupByFieldChanged(groupBy) {
    dispatch({
      type: LOAD_PERSON_DAILY_ACTIVITY,
      payload: { filter, groupBy },
    });
  }

  function handleFilterFieldChanged(name, value) {
    const newFilter =
      name in filter
        ? {
            ...filter,
            [name]: (value || []).map((value) => value.value),
          }
        : {
            ...filter,
            areas: {
              ...filter.areas,
              [name]: (value || []).map((value) => value.value),
            },
          };

    dispatch({
      type: LOAD_PERSON_DAILY_ACTIVITY,
      payload: { filter: newFilter, groupBy },
    });
  }

  function handleMileageTypeFieldChanged(mileageType) {
    setMileageType(mileageType);
  }

  function handleBaseVisitCountTypeFieldChanged(baseVisitCountType) {
    setBaseVisitCountType(baseVisitCountType);
  }

  function handleLegendClick(selectedBar) {
    const index = hiddenBars.indexOf(selectedBar);

    if (index === -1) {
      setHiddenBars(hiddenBars.concat(selectedBar));
    } else {
      setHiddenBars(
        hiddenBars.slice(0, index).concat(hiddenBars.slice(index + 1))
      );
    }
  }

  function handleSortMenuOpen(target) {
    setSortMenuAnchor(target);
  }

  function handleSortMenuClose() {
    setSortMenuAnchor(null);
  }

  function handleSortItemClick(orderBy) {
    dispatch({
      type: SORT_PERSON_DAILY_ACTIVITY,
      payload: orderBy,
    });
    setSortMenuAnchor(null);
  }

  function handlePageChange(event, newPage) {
    setPage(newPage);
  }

  function handleRowsPerPageChange(event) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  function handleOrderChange(order) {
    setOrder(order);
  }

  function handleOrderByChange(orderBy) {
    setOrderBy(orderBy);
    setOrder('asc');
  }

  async function handleDownloadClick() {
    const filename = 'Person Daily Activity.csv';

    downloadCSV(data, filename, headers);
  }

  const xAxisHeight =
    data.length === 0
      ? 0
      : getTextWidth(
          data.map((item) => item.group).sort((a, b) => b.length - a.length)[0],
          '12px Roboto'
        ) + 16;

  return (
    <Container title="Daily Activity">
      <Parameters
        onFetch={handleFetch}
        onCancel={handleCancel}
        isFetching={isLoading}
        className={classes.parameters}
        pointEvent
        dateOnly
        person
      />
      <div className={classes.itemSection}>
        {' '}
        <Helmet>
          <title>IR3 | Daily Activity</title>
        </Helmet>
        <Toolbar className={classes.toolbar}>
          <Typography className={classes.cardHeader} variant="subtitle1">
            Daily Activity by
          </Typography>
          <TextField
            select
            className={classes.groupByField}
            name="groupBy"
            value={groupBy}
            onChange={(event) => handleGroupByFieldChanged(event.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {Object.values(groupByValues).map((value) => (
              <MenuItem key={value} value={value}>
                {getKeyLabel(value)}
              </MenuItem>
            ))}
          </TextField>
          <div className={classes.flex} />
          <IconButton
            title={showFilter ? 'Hide filter' : 'Show filter'}
            onClick={() => handleFilterToggle()}
          >
            <FilterListIcon color={showFilter ? 'primary' : 'inherit'} />
          </IconButton>
          <IconButton
            title="Sort by"
            aria-owns={sortMenuAnchor ? 'date-menu' : undefined}
            aria-haspopup="true"
            onClick={(event) => handleSortMenuOpen(event.target)}
          >
            <SortIcon />
          </IconButton>
          <Menu
            id="date-menu"
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={() => handleSortMenuClose()}
          >
            <MenuItem onClick={() => handleSortItemClick('Group')}>
              {getKeyLabel(groupBy)}
            </MenuItem>
            <Divider />
            {orderByValues.map((orderBy) => (
              <MenuItem
                key={orderBy}
                onClick={() => handleSortItemClick(orderBy)}
              >
                {labels.get(orderBy)}
              </MenuItem>
            ))}
            <MenuItem onClick={() => handleSortItemClick(mileageType)}>
              {labels.get(mileageType)}
            </MenuItem>
            <MenuItem onClick={() => handleSortItemClick(baseVisitCountType)}>
              {labels.get(baseVisitCountType)}
            </MenuItem>
          </Menu>
          <IconButton
            title="Download data"
            disabled={data.length === 0}
            onClick={handleDownloadClick}
          >
            <GetAppIcon />
          </IconButton>
        </Toolbar>
        <Collapse in={showFilter} timeout="auto">
          <div className={classes.collapseContainer}>
            {useReducedResourceInformation ? (
              <SelectMultiple
                fullWidth
                label="Staff ID"
                placeholder="Select..."
                value={filter.code.map((value) => ({
                  label: value,
                  value,
                }))}
                onChange={(value) => handleFilterFieldChanged('code', value)}
                suggestions={filterValues.code.map((value) => ({
                  label: value,
                  value,
                }))}
              />
            ) : (
              <Fragment>
                <SelectMultiple
                  fullWidth
                  label="Name"
                  placeholder="Select..."
                  value={filter.name.map((value) => ({
                    label: value,
                    value,
                  }))}
                  onChange={(value) => handleFilterFieldChanged('name', value)}
                  suggestions={filterValues.name.map((value) => ({
                    label: value,
                    value,
                  }))}
                />
                <SelectMultiple
                  fullWidth
                  label="Role"
                  placeholder="Select..."
                  value={filter.role.map((value) => ({
                    label: value,
                    value,
                  }))}
                  onChange={(value) => handleFilterFieldChanged('role', value)}
                  suggestions={filterValues.role.map((value) => ({
                    label: value,
                    value,
                  }))}
                />
              </Fragment>
            )}
            {Object.entries(filterValues.areas).map((entry) => {
              return (
                <SelectMultiple
                  fullWidth
                  key={entry[0]}
                  label={_.startCase(entry[0])}
                  placeholder="Select..."
                  value={(filter.areas[entry[0]] || []).map((value) => ({
                    label: value,
                    value,
                  }))}
                  onChange={(value) =>
                    handleFilterFieldChanged(entry[0], value)
                  }
                  suggestions={entry[1].map((value) => ({
                    label: value,
                    value,
                  }))}
                />
              );
            })}
          </div>
        </Collapse>
        <Paper className={classes.card}>
          <CardContent className={classes.cardContent}>
            <div className={classes.legend}>
              {bars.map((bar) => (
                <div
                  key={bar.name}
                  className={classes.legendItem}
                  onClick={() => handleLegendClick(bar.name)}
                >
                  <Avatar
                    className={classes.legendAvatar}
                    style={{
                      backgroundColor:
                        !hiddenBars.includes(bar.name) && bar.colour,
                    }}
                  >
                    <Fragment />
                  </Avatar>
                  <Typography variant="caption">
                    {labels.get(bar.name)}
                  </Typography>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="99%" height={520}>
              <BarChart
                data={data}
                margin={{ top: 0, right: 16, left: 0, bottom: 32 }}
                barGap={0}
                barCategoryGap={16}
              >
                <XAxis
                  dataKey="group"
                  angle={-90}
                  textAnchor="end"
                  interval={0}
                  height={xAxisHeight}
                >
                  <Label
                    value={getKeyLabel(groupBy)}
                    offset={36}
                    position="bottom"
                  />
                </XAxis>
                <YAxis>
                  <Label
                    value="Hours"
                    angle={-90}
                    position="left"
                    offset={-24}
                  />
                </YAxis>
                <Tooltip
                  cursor={data.length > 0}
                  content={<CustomTooltip unit="hours" />}
                />
                {data.length > 0 && <Brush dataKey="Date" height={24} />}
                {bars.map((bar) => (
                  <Bar
                    key={bar.name}
                    dataKey={bar.name}
                    name={labels.get(bar.name)}
                    fill={bar.colour}
                    hide={hiddenBars.includes(bar.name)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Paper>
        <Toolbar className={classes.toolbar}>
          <TextField
            select
            className={classes.typeField}
            name="mileageType"
            value={mileageType}
            onChange={(event) =>
              handleMileageTypeFieldChanged(event.target.value)
            }
          >
            {measureTypes.map((measureType) => (
              <MenuItem
                key={measureType}
                value={`${measureType}DrivingMileage`}
              >
                {labels.get(`${measureType}DrivingMileage`)}
              </MenuItem>
            ))}
          </TextField>
          <Typography className={classes.cardHeader} variant="subtitle1">
            by {getKeyLabel(groupBy)}
          </Typography>
        </Toolbar>
        <Paper className={classes.card}>
          <CardContent className={classes.cardContent}>
            <ResponsiveContainer width="99%" height={520}>
              <BarChart
                data={data}
                margin={{ top: 0, right: 16, left: 8, bottom: 32 }}
                barGap={0}
                barCategoryGap={4}
              >
                <XAxis
                  dataKey="group"
                  angle={-90}
                  textAnchor="end"
                  interval={0}
                  height={xAxisHeight}
                >
                  <Label
                    value={getKeyLabel(groupBy)}
                    offset={36}
                    position="bottom"
                  />
                </XAxis>
                <YAxis>
                  <Label
                    value="Miles"
                    angle={-90}
                    position="left"
                    offset={-16}
                  />
                </YAxis>
                <Tooltip cursor={data.length > 0} content={<CustomTooltip />} />
                {data.length > 0 && <Brush dataKey="Name" height={24} />}
                {measureTypes.map((measureType) => (
                  <Bar
                    key={measureType}
                    dataKey={`${measureType}DrivingMileage`}
                    name={labels.get(`${measureType}DrivingMileage`)}
                    fill={indigo[800]}
                    hide={mileageType !== `${measureType}DrivingMileage`}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Paper>
        <Toolbar className={classes.toolbar}>
          <TextField
            select
            className={classes.typeField}
            name="tripCountType"
            value={baseVisitCountType}
            onChange={(event) =>
              handleBaseVisitCountTypeFieldChanged(event.target.value)
            }
          >
            {measureTypes.map((measureType) => (
              <MenuItem key={measureType} value={`${measureType}BaseVisits`}>
                {labels.get(`${measureType}BaseVisits`)}
              </MenuItem>
            ))}
          </TextField>
          <Typography className={classes.cardHeader} variant="subtitle1">
            by {getKeyLabel(groupBy)}
          </Typography>
        </Toolbar>
        <Paper className={classes.card}>
          <CardHeader subheader="Total by Name" />
          <CardContent className={classes.cardContent}>
            <ResponsiveContainer width="99%" height={520}>
              <BarChart
                data={data}
                margin={{ top: 0, right: 16, left: 0, bottom: 32 }}
                barCategoryGap={4}
              >
                <XAxis
                  dataKey="group"
                  angle={-90}
                  textAnchor="end"
                  interval={0}
                  height={xAxisHeight}
                >
                  <Label
                    value={getKeyLabel(groupBy)}
                    offset={36}
                    position="bottom"
                  />
                </XAxis>
                <YAxis>
                  <Label
                    value="Visits"
                    angle={-90}
                    position="left"
                    offset={-24}
                  />
                </YAxis>
                <Tooltip cursor={data.length > 0} content={<CustomTooltip />} />
                {data.length > 0 && <Brush dataKey="Name" height={24} />}
                {measureTypes.map((measureType) => (
                  <Bar
                    key={measureType}
                    dataKey={`${measureType}BaseVisits`}
                    name={labels.get(`${measureType}BaseVisits`)}
                    fill={indigo[800]}
                    hide={baseVisitCountType !== `${measureType}BaseVisits`}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Paper>
        <Toolbar className={classes.toolbar}>
          <Typography className={classes.cardHeader} variant="subtitle1">
            Daily Activity by
          </Typography>
          <TextField
            select
            className={classes.groupByField}
            name="groupBy"
            value={groupBy}
            onChange={(event) => handleGroupByFieldChanged(event.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {Object.values(groupByValues).map((value) => (
              <MenuItem key={value} value={value}>
                {getKeyLabel(value)}
              </MenuItem>
            ))}
          </TextField>
        </Toolbar>
        <Paper className={classes.card}>
          <Table
            classes={classes}
            data={data}
            headers={headers}
            rowsPerPage={rowsPerPage}
            page={page}
            dense={true}
            order={order}
            orderBy={orderBy}
            onOrderChange={handleOrderChange}
            onOrderByChange={handleOrderByChange}
          />
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />{' '}
        </Paper>
      </div>
    </Container>
  );
}
